import Button, { ButtonTypes } from '@/components/Buttons/Button';
import {
  Collapse,
  Divider,
  Flex,
  Select,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { IconBookmark, IconSearch, IconTag, IconX } from '@tabler/icons-react';
import { emojiSource, isDark, useReplaceQueryParams } from '@/utils/utils';
import { useEffect, useRef, useState } from 'react';
import {
  useGetTechblogCategoryList,
  useGetTechblogRecommendList,
} from '@/features/Techblog/hook/api';

import { BadgeButton } from '@/components/Badge';
import Card from '@/components/Card';
import Image from 'next/image';
import Link from 'next/link';
import themeVariables from '@/styles/themeVariables';
import { useHandleBlogKeyword } from '../hook/useTechblogSearchMethod';
import { useTechblogListPageSideBoxStyle } from '@/features/Techblog/techblog.style';

const TechblogSearchBox = () => {
  const { dark } = isDark();
  const { classes } = useTechblogListPageSideBoxStyle({ dark });

  const { queryParams } = useReplaceQueryParams();

  const { data: categoryList } = useGetTechblogCategoryList();
  const { data: recommendList } = useGetTechblogRecommendList();

  const [preKeyword, setPreKeyword] = useState(
    () => queryParams.get('keyword') || ''
  );

  const {
    tag,
    handleInitKeyword,
    handleKeywordChange,
    handleTagChange,
    handleEnterKeyword,
  } = useHandleBlogKeyword([preKeyword, setPreKeyword]);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef?.current?.blur();
  }, [queryParams.get('keyword')]);

  return (
    <Card noShadow>
      <div className={classes.cardContainer}>
        <div className={classes.headerContainer}>
          <Flex gap={15} align={'center'}>
            <Image
              width={24}
              height={24}
              src={emojiSource('1f50e', 'apple')}
              alt="emoji"
            />
            <Text className={classes.headerTitle}>검색하기</Text>
          </Flex>
          <Flex justify={'flex-end'} align={'center'}>
            <Link href={'/techblog/favorite-list'}>
              <Button
                type={ButtonTypes.bordered}
                leftIcon={
                  <IconBookmark
                    size={18}
                    fill={themeVariables.colors.red300}
                    color={themeVariables.colors.red300}
                  />
                }
                style={{
                  height: 34,
                  fontWeight: 500,
                  fontSize: 12,
                  padding: '0px 12px',
                  backgroundColor: dark ? 'transparent' : '#fff',
                  border: `1px solid ${themeVariables.lineDark.light300(dark)}`,
                  color: themeVariables.darkMode.normal(dark),
                }}
              >
                MY 북마크
              </Button>
            </Link>
          </Flex>
        </div>
        <Collapse in={true} transitionDuration={400}>
          <div className={classes.searchBoxContainer}>
            <Tabs
              variant={'pills'}
              defaultValue={queryParams.get('keyword') ? 'keyword' : 'tag'}
            >
              <Tabs.List className={classes.tabContainer}>
                <Tabs.Tab className={classes.tab} value={'tag'}>
                  태그로 검색
                </Tabs.Tab>
                <Tabs.Tab className={classes.tab} value={'keyword'}>
                  키워드로 검색
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value={'tag'}>
                <Select
                  searchable
                  variant={'filled'}
                  value={tag}
                  placeholder={'기술 스택을 선택해주세요'}
                  onChange={handleTagChange}
                  data={(categoryList || [])?.map((techstack) => ({
                    label: techstack.name,
                    value: techstack.id,
                  }))}
                />
                <Divider
                  my={24}
                  color={themeVariables.lineDark.light200(dark)}
                />
                <div className={classes.searchBoxRecommendedTagTitle}>
                  <IconTag size={18} /> 어제 가장 많이 검색된 기술
                </div>
                <div className={classes.searchBoxTagContainer}>
                  {recommendList?.map((tag) => (
                    <BadgeButton
                      key={tag.name}
                      text={tag.name}
                      onClick={() => {
                        handleTagChange(tag.id);
                      }}
                    />
                  ))}
                </div>
              </Tabs.Panel>
              <Tabs.Panel value={'keyword'}>
                <TextInput
                  ref={inputRef}
                  value={preKeyword}
                  variant={'filled'}
                  placeholder={'키워드를 입력해주세요'}
                  onKeyDown={handleEnterKeyword}
                  onChange={(e) => {
                    setPreKeyword(e.target.value);
                  }}
                />
                <div className={classes.searchBoxKeywordTabButtonContainer}>
                  <Button
                    type={ButtonTypes.gray}
                    leftIcon={<IconX size={16} />}
                    style={{ height: 40 }}
                    onClick={handleInitKeyword}
                  >
                    초기화
                  </Button>
                  <Button
                    type={ButtonTypes.gray}
                    leftIcon={<IconSearch size={16} />}
                    style={{ height: 40 }}
                    onClick={handleKeywordChange}
                  >
                    검색
                  </Button>
                </div>
              </Tabs.Panel>
            </Tabs>
          </div>
        </Collapse>
      </div>
    </Card>
  );
};

export default TechblogSearchBox;
